"use client";

import { useRef, useState } from "react";
import Link from "next/link";
import toast from "react-hot-toast";
import DropdownWithSearch from "@/app/components/appcomponents/DropdownWithSearch";
import TextFieldComp from "@/app/components/appcomponents/TextFieldComp";
import { PopupShell } from "@/app/components/appcomponents/thankYouWizard/parts";
import pastMemorialService from "@/services/past-memorial-service";
import { useAuth } from "@/hooks/useAuth";
import SelectField from "./SelectField";
import { PrimaryButton, SecondaryButton } from "./PopupButtons";
import DeceasedDatesFields, { datesError, datesPayload, datesText, emptyDates } from "./DeceasedDatesFields";
import { PAST_OPTIONS, PAST_PROMO_END } from "./orderConfig";

const ALLOWED_DOCUMENTS = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
const ALLOWED_PHOTOS = ["image/jpeg", "image/png", "image/webp", "image/avif"];
const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
// The page is for people who died before 2026; newer deaths get a normal obituary.
const LAST_DEATH_DATE = new Date(2025, 11, 31);
const PASSWORD_MIN = 6;

// A box with one blue button for a file (the death document, or the optional photo of the deceased).
function FileBox({ file, onPick, buttonText, hint, accept, allowed, wrongTypeText, maxBytes }) {
  const input = useRef(null);
  return (
    <div className="rounded-[10px] bg-[#F0F4F8] px-4 py-5 text-center shadow-custom-dark-to-white">
      <input
        ref={input}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const picked = e.target.files?.[0];
          if (!picked) return;
          if (!allowed.includes(picked.type)) {
            toast.error(wrongTypeText);
          } else if (maxBytes && picked.size > maxBytes) {
            toast.error("Slika je prevelika (največ 5 MB)");
          } else {
            onPick(picked);
          }
          e.target.value = "";
        }}
      />
      <button
        type="button"
        onClick={() => input.current?.click()}
        className="h-[40px] w-[220px] max-w-full rounded-[4px] bg-gradient-to-b from-[#0D94E8] to-[#0A85C2] text-[16px] text-white shadow-custom-dark-bottom"
      >
        {buttonText}
      </button>
      <p className="mt-2 text-[11px] leading-[14px] text-[#939393]">{file ? file.name : hint}</p>
    </div>
  );
}

function Nav({ onBack, onNext, nextLabel = "Naprej", loading = false }) {
  return (
    <div className="mt-8 flex items-center justify-between gap-3">
      {onBack ? <SecondaryButton onClick={onBack}>Nazaj</SecondaryButton> : <span />}
      <PrimaryButton onClick={onNext} loading={loading}>
        {nextLabel}
      </PrimaryButton>
    </div>
  );
}

// A text field whose placeholder has a smaller second part: "Kraj" and "(to bo izpisano)".
function Field({ main, note, value, onChange, maxLength = 100, readOnly = false, background }) {
  return (
    <div className="relative">
      <TextFieldComp placeholder="" value={value} onChange={onChange} maxLength={maxLength} readOnly={readOnly} background={background} />
      {!value && (
        <span className="pointer-events-none absolute left-[19px] top-[14px] text-[15px] leading-[20px] text-[#6D778E]">
          {main}
          {note ? <span className="ml-1 text-[12px]">{note}</span> : null}
        </span>
      )}
    </div>
  );
}

// A line of the last look (step 4): not an input, so a flat different background; changes are made on the earlier steps.
function ReadOnlyLine({ children }) {
  return (
    <div className="flex min-h-[34px] items-center rounded-[6px] bg-[#EDF0F4] px-4 text-[14px] leading-[18px] text-[#6D778E]">
      {children}
    </div>
  );
}

/**
 * "Spominska za davno pokojne": a memory page (no obituary) for somebody who died years or decades ago.
 * Four steps; the last one opens the Stripe checkout. Nothing is published by this popup: the page is created
 * when an admin has checked the document (see the server's /past-memorials). It does not close when the visitor
 * clicks outside it: only the X does, so what was typed is never lost.
 */
export default function PastMemorialModal({ onClose }) {
  const { isAuthenticated } = useAuth();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [relation, setRelation] = useState("");
  const [applicantName, setApplicantName] = useState("");
  const [name, setName] = useState("");
  const [sirName, setSirName] = useState("");
  const [location, setLocation] = useState("");
  const [city, setCity] = useState("");
  const [region, setRegion] = useState("");
  const [photo, setPhoto] = useState(null);
  const [dates, setDates] = useState(emptyDates);
  const [document, setDocument] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);
  const [password, setPassword] = useState("");
  const [product, setProduct] = useState("past_2y");

  if (!isAuthenticated) {
    return (
      <PopupShell title="Spominska stran" titleInside compact closeOnBackdrop={false} onClose={onClose}>
        <p className="text-[18px] leading-[26px] text-[#1E2125]">Za naročilo spominske strani se moraš prijaviti.</p>
        <div className="mx-auto mt-[32px] w-[250px] mobile:w-full">
          <Link href="/registracija" className="flex h-[44px] w-full items-center justify-center rounded-[4px] bg-gradient-to-b from-[#0D94E8] to-[#0A85C2] text-[16px] text-white shadow-custom-dark-bottom">
            Prijava
          </Link>
        </div>
      </PopupShell>
    );
  }

  const fail = (message) => {
    toast.error(message);
    return false;
  };

  // Each step is checked before the next opens; the server checks everything again.
  const checkStep = (n) => {
    if (n === 1) {
      if (!relation.trim()) return fail("Vpiši svoje sorodstvo s pokojnim");
      if (!applicantName.trim()) return fail("Vpiši svoje ime in priimek");
    }
    if (n === 2) {
      if (!name.trim() || !sirName.trim()) return fail("Vpiši ime in priimek pokojnika");
      if (!location.trim()) return fail("Vpiši kraj");
      if (!city) return fail("Izberi občino");
    }
    if (n === 3) {
      // As on the obituary form: the death is required (a year is enough), the birth is optional.
      const problem = datesError(dates);
      if (problem) return fail(problem);
      if (!document) return fail("Priloži uradni dokument o smrti ali sliko nagrobnika");
      if (!confirmed) return fail("Potrdi, da so podatki resnični");
    }
    return true;
  };
  const next = () => checkStep(step) && setStep(step + 1);
  const back = () => setStep(step - 1);

  const submit = async () => {
    if (busy) return;
    if (isPrivate && password.trim().length < PASSWORD_MIN) {
      toast.error(`Geslo mora imeti vsaj ${PASSWORD_MIN} znakov`);
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("product", product);
      form.append("relation", relation.trim());
      form.append("applicantName", applicantName.trim());
      form.append("name", name.trim());
      form.append("sirName", sirName.trim());
      form.append("location", location.trim());
      form.append("city", city);
      Object.entries(datesPayload(dates)).forEach(([key, value]) => {
        if (value !== "" && value != null) form.append(key, value);
      });
      form.append("confirmed", "true");
      form.append("isPrivate", isPrivate ? "true" : "false");
      if (isPrivate) form.append("password", password.trim());
      form.append("document", document);
      if (photo) form.append("picture", photo);
      const { paymentUrl } = await pastMemorialService.create(form);
      if (!paymentUrl) throw new Error("Plačila trenutno ni mogoče pripraviti. Poskusi znova.");
      window.location.href = paymentUrl;
      return;
    } catch (error) {
      toast.error(error?.message || "Prišlo je do napake. Poskusi znova.", { duration: 9000 });
    }
    setBusy(false);
  };

  const shell = { compact: true, closeOnBackdrop: false, onClose };
  const stepLabel = <p className="mb-3 text-right text-[14px] text-[#6D778E]">Korak {step}/4</p>;
  const gap = <div className="mt-5" />;

  if (step === 1) {
    return (
      <PopupShell title="Spominska stran" {...shell}>
        <h3 className="text-[20px] leading-[26px] text-[#1E2125] mobile:text-[18px] mobile:leading-[24px]">
          Za bližnje, ki so se poslovili pred leti
        </h3>
        <div className="mt-4 rounded-[8px] bg-[#E8EBF0] px-3 py-3 text-[12px] leading-[18px] text-[#6D778E]">
          <p className="flex items-center gap-2 text-[14px] leading-[20px] text-[#14105F]"><span aria-hidden="true" className="inline-flex h-[14px] w-[14px] shrink-0 items-center justify-center rounded-full border border-[#14105F] text-[9px]">?</span>Vse je enako, le brez osmrtnice</p>
          <p className="mt-2">
            Op: Za izdelavo spominske je treba priložiti dokument o smrti, takrat objavljeno osmrtnico ali pa samo sliko nagrobnika ter potrditi svoje sorodstvo s pokojnim.
          </p>
          <p className="mt-3">
            S tem želimo preprečiti zlorabe, objavljanje neresničnih podatkov in neželene vsebine ter zagotoviti, da je skrbništvo zaupano osebi, ki je bila pokojnemu blizu. Zahteva je namenjena zaščiti pokojnikovih bližnjih, njihovega dostojanstva in varnega ohranjanja spomina.
          </p>
        </div>
        <div className="mt-8">
          <TextFieldComp placeholder="Vaše sorodstvo s pokojnim" value={relation} onChange={(e) => setRelation(e.target.value)} maxLength={100} />
          {gap}
          <TextFieldComp placeholder="Vaše ime in priimek" value={applicantName} onChange={(e) => setApplicantName(e.target.value)} maxLength={100} />
        </div>
        <Nav onNext={next} />
      </PopupShell>
    );
  }

  if (step === 2) {
    return (
      <PopupShell title="Podatki o pokojniku" {...shell}>
        {stepLabel}
        <TextFieldComp placeholder="Ime" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} />
        {gap}
        <TextFieldComp placeholder="Priimek" value={sirName} onChange={(e) => setSirName(e.target.value)} maxLength={100} />
        {gap}
        <Field main="Kraj" note="(to bo izpisano)" value={location} onChange={(e) => setLocation(e.target.value)} />
        {gap}
        <DropdownWithSearch
          fieldLook
          placeholder={<>Občina <span className="ml-1 text-[12px]">(za lažje iskanje po imenu)</span></>}
          selectedCity={city}
          selectedRegion={region}
          onSelectCity={(value) => setCity(value || "")}
          onSelectRegion={(value) => setRegion(value || "")}
        />
        <div className="mt-6">
          <FileBox
            file={photo}
            onPick={setPhoto}
            buttonText="Slika pokojnika/ce"
            hint="Ni obvezna, je pa dobrodošla za spominsko stran"
            accept="image/*"
            allowed={ALLOWED_PHOTOS}
            wrongTypeText="Dovoljene so slike JPG, PNG ali WEBP."
            maxBytes={MAX_PHOTO_BYTES}
          />
        </div>
        <Nav onBack={back} onNext={next} />
      </PopupShell>
    );
  }

  if (step === 3) {
    return (
      <PopupShell title="Podatki o pokojniku" {...shell}>
        {stepLabel}
        <DeceasedDatesFields value={dates} onChange={setDates} lastDate={LAST_DEATH_DATE} />
        <div className="mt-6">
          <FileBox
            file={document}
            onPick={setDocument}
            buttonText="Dokument ali slika"
            hint="Dokument, objavljena osmrtnica ali slika nagrobnika"
            accept="image/*,application/pdf"
            allowed={ALLOWED_DOCUMENTS}
            wrongTypeText="Dovoljene so samo slike in PDF datoteke."
          />
        </div>
        <label className="mt-5 flex cursor-pointer items-center gap-3 text-[15px] text-[#414B5A]">
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="h-[22px] w-[22px] shrink-0" />
          Potrjujem, da so podatki resnični
        </label>
        <Nav onBack={back} onNext={next} />
      </PopupShell>
    );
  }

  return (
    <PopupShell title="Zadnji pregled" {...shell}>
      {stepLabel}
      <ReadOnlyLine>{[`${name.trim()} ${sirName.trim()}`, location.trim()].filter(Boolean).join(", ")}</ReadOnlyLine>
      <div className="mt-3" />
      <ReadOnlyLine>{datesText(dates)}</ReadOnlyLine>
      <label className="mt-6 flex cursor-pointer items-center gap-3 text-[15px] text-[#414B5A]">
        <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} className="h-[22px] w-[22px] shrink-0" />
        Želim, da je stran zasebna
      </label>
      <div className="mt-3">
        <Field
          main="Izberi geslo"
          note="(vsaj 6 črk ali številk)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          readOnly={!isPrivate}
          background={isPrivate ? "#F2F8FF66" : "#E8EBF0"}
        />
      </div>
      <p className="mt-6 text-right text-[13px] text-[#6D778E]">Podvojena doba (do vključno {PAST_PROMO_END})</p>
      <div className="mt-1">
        <SelectField label="Izberi Skrbnika" value={product} onChange={setProduct} options={PAST_OPTIONS} centeredChevron />
      </div>
      <Nav onBack={back} onNext={submit} nextLabel="Naročilo" loading={busy} />
    </PopupShell>
  );
}
